/**
 * Thread Add-People — pure model for the "add participant" affordance
 * (group-threading P2c).
 *
 * Given the account roster (`accounts/{acct}/members`), the thread's current
 * participant set, and the signed-in actor, derive who can be added. Pure +
 * deterministic + dependency-free of React so it unit-tests in isolation (mirrors
 * `threadChoicePanel/panelModel`). This layer NEVER writes membership and NEVER
 * authorizes — the server (`threadMembershipWrite`) is the sole authority; this only
 * shapes the picker. `addable` is fail-closed: self and current participants are
 * always excluded.
 */

/** Minimal structural roster entry — `MemberRecord` satisfies this. */
export interface AddPeopleRosterEntry {
  userId: string;
  displayName?: string;
  email?: string;
  /**
   * Whether the workspace will admit this member to a thread. Absent means yes:
   * legacy member docs carry no status and ARE active, and the thread write path
   * applies the same tolerance. An explicit `false` (suspended / not yet joined)
   * moves the person to `unavailable` — offering an Add button that the server is
   * guaranteed to reject is worse than saying why.
   */
  active?: boolean;
  /** Raw membership status, surfaced as the reason a member is unavailable. */
  status?: string;
}

/** Someone invited to the workspace who hasn't accepted yet. */
export interface AddPeoplePendingInvite {
  inviteId: string;
  email: string;
  displayName?: string;
  status: 'pending' | 'expired';
}

export interface AddPeopleCandidate {
  userId: string;
  /** Display label: displayName → email → raw uid. */
  label: string;
  /** Email shown as a secondary line only when a distinct displayName exists. */
  secondary?: string;
  /** Already a participant of this thread (shown disabled / checked). */
  alreadyParticipant: boolean;
  /** The signed-in actor (never addable to their own thread). */
  isSelf: boolean;
  /** Set on `unavailable` rows: short, human reason this person can't be added. */
  blockedReason?: string;
}

export interface AddPeopleModel {
  /** Addable candidates: roster minus self minus current participants, label-sorted. */
  addable: AddPeopleCandidate[];
  /** Roster members already in the thread (display-only: "already here"). */
  existing: AddPeopleCandidate[];
  /** Roster members the workspace won't admit yet, with the reason (display-only). */
  unavailable: AddPeopleCandidate[];
  /** Invited but not yet joined — shown so "where is the person I invited?" is answered. */
  pending: AddPeoplePendingInvite[];
  /** Convenience: `addable.length > 0`. */
  hasAddable: boolean;
  /** Unique roster members seen (audit / empty-state copy). */
  rosterCount: number;
  /** True when there is anything at all to show besides the empty state. */
  hasAnyone: boolean;
}

function trim(value: unknown): string {
  return String(value ?? '').trim();
}

function toCandidate(
  entry: AddPeopleRosterEntry,
  participantSet: ReadonlySet<string>,
  selfId: string,
): AddPeopleCandidate {
  const userId = trim(entry.userId);
  const name = trim(entry.displayName);
  const email = trim(entry.email);
  const label = name || email || userId;
  const secondary = name && email ? email : undefined;
  return {
    userId,
    label,
    secondary,
    alreadyParticipant: participantSet.has(userId),
    isSelf: !!selfId && userId === selfId,
  };
}

function byLabel(a: AddPeopleCandidate, b: AddPeopleCandidate): number {
  const cmp = a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase());
  return cmp !== 0 ? cmp : a.userId.localeCompare(b.userId);
}

/** Short, non-jargon reason a roster member isn't addable right now. */
export function describeInactiveMember(status: string | undefined): string {
  switch ((status ?? '').trim().toLowerCase()) {
    case 'invited':
      return "Hasn't accepted their invitation yet";
    case 'suspended':
      return 'Suspended — reactivate them in Team & Roles';
    case 'removed':
      return 'No longer in this workspace';
    default:
      return 'Not an active member of this workspace yet';
  }
}

/**
 * Derive the add-people model. De-dupes the roster by uid (first wins), excludes
 * self + current participants from `addable`, and sorts `addable` by label. Blank
 * uids are dropped. Purely presentational — the server re-checks admittance.
 *
 * Members the workspace won't admit (explicit `active: false`) are routed to
 * `unavailable` WITH a reason rather than dropped, and pending invitations are
 * carried through untouched. Both exist so the picker can always answer "why
 * isn't the person I just added to my team here?" — silently showing nothing was
 * the original defect.
 */
export function deriveAddPeopleModel(args: {
  roster: readonly AddPeopleRosterEntry[];
  currentParticipantIds: readonly string[];
  selfId: string;
  pendingInvites?: readonly AddPeoplePendingInvite[];
}): AddPeopleModel {
  const selfId = trim(args.selfId);
  const participantSet = new Set<string>();
  for (const id of args.currentParticipantIds ?? []) {
    const norm = trim(id);
    if (norm) participantSet.add(norm);
  }

  const seen = new Set<string>();
  const addable: AddPeopleCandidate[] = [];
  const existing: AddPeopleCandidate[] = [];
  const unavailable: AddPeopleCandidate[] = [];
  let rosterCount = 0;

  for (const entry of args.roster ?? []) {
    if (!entry) continue;
    const candidate = toCandidate(entry, participantSet, selfId);
    if (!candidate.userId || seen.has(candidate.userId)) continue;
    seen.add(candidate.userId);
    rosterCount += 1;

    // Self is never shown — you don't add (or "already here") yourself in a picker
    // meant to bring OTHER people in.
    if (candidate.isSelf) continue;
    if (candidate.alreadyParticipant) {
      existing.push(candidate);
      continue;
    }
    // Only an EXPLICIT false blocks — an absent flag means a legacy member doc
    // with no status field, which this workspace does admit.
    if (entry.active === false) {
      unavailable.push({ ...candidate, blockedReason: describeInactiveMember(entry.status) });
      continue;
    }
    addable.push(candidate);
  }

  addable.sort(byLabel);
  existing.sort(byLabel);
  unavailable.sort(byLabel);

  const pending = [...(args.pendingInvites ?? [])];

  return {
    addable,
    existing,
    unavailable,
    pending,
    hasAddable: addable.length > 0,
    rosterCount,
    hasAnyone:
      addable.length > 0 || existing.length > 0 || unavailable.length > 0 || pending.length > 0,
  };
}
